import { useState } from 'react'
import { ClipboardList, FileCheck2, Plus, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { SoftButton, ds } from '@/ui/design-system'

export const emptyImportantInfos = () => ({ consignes: [], formalites: [] })

export const normalizeImportantInfos = (raw) => {
  let value = raw
  if (typeof value === 'string') {
    try {
      value = JSON.parse(value)
    } catch {
      value = null
    }
  }
  const asList = (items) =>
    (Array.isArray(items) ? items : [])
      .map((item) => {
        if (typeof item === 'string') return item.trim()
        if (item && typeof item === 'object') {
          return String(item.text || item.label || item.title || '').trim()
        }
        return ''
      })
      .filter(Boolean)
  return {
    consignes: asList(value?.consignes),
    formalites: asList(value?.formalites),
  }
}

const ListEditor = ({
  id,
  title,
  hint,
  icon: Icon,
  accentClass,
  items,
  onChange,
  addPlaceholder,
  emptyTitle,
  emptyHint,
}) => {
  const { t } = useTranslation()
  const [draft, setDraft] = useState('')

  const handleAdd = () => {
    const value = draft.trim()
    if (!value) return
    onChange([...items, value])
    setDraft('')
  }

  const handleRemove = (index) => {
    onChange(items.filter((_, i) => i !== index))
  }

  return (
    <section className="flex h-full flex-col overflow-hidden rounded-[var(--radius-md)] border border-[var(--border)] bg-[var(--card)]">
      <header className="flex items-start gap-3 border-b border-[var(--border)] px-4 py-3">
        <span
          className={`grid h-9 w-9 shrink-0 place-items-center rounded-md ${accentClass}`}
          aria-hidden
        >
          {Icon ? <Icon className="h-4 w-4" /> : null}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h4 className="m-0 text-sm font-semibold text-[var(--foreground)]">{title}</h4>
            <span className="rounded-md bg-[var(--muted)] px-2 py-0.5 text-xs font-medium tabular-nums text-[var(--muted-foreground)]">
              {items.length}
            </span>
          </div>
          {hint ? <p className={`mt-1 mb-0 ${ds.textMuted}`}>{hint}</p> : null}
        </div>
      </header>

      <div className="flex flex-1 flex-col gap-3 p-4">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <label className="sr-only" htmlFor={id}>
            {addPlaceholder}
          </label>
          <input
            id={id}
            className={ds.input}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                handleAdd()
              }
            }}
            placeholder={addPlaceholder}
          />
          <SoftButton
            type="button"
            variant="secondary"
            className="w-full shrink-0 sm:w-auto"
            icon={<Plus className="h-4 w-4" aria-hidden />}
            onClick={handleAdd}
            disabled={!draft.trim()}
          >
            {t('common.add', { defaultValue: 'Ajouter' })}
          </SoftButton>
        </div>

        {items.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center rounded-lg border border-dashed border-[var(--border)] bg-[var(--muted)]/40 px-3 py-8 text-center">
            <p className="m-0 text-sm font-medium text-[var(--foreground)]">{emptyTitle}</p>
            {emptyHint ? <p className={`mt-1 mb-0 max-w-xs ${ds.textMuted}`}>{emptyHint}</p> : null}
          </div>
        ) : (
          <ul className="m-0 list-none space-y-2 p-0" aria-label={title}>
            {items.map((item, index) => (
              <li
                key={`${item}-${index}`}
                className="flex items-start gap-2 rounded-lg border border-[var(--border)] bg-[var(--background)] px-3 py-2"
              >
                <span
                  className="mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-md bg-[var(--muted)] text-[0.7rem] font-semibold tabular-nums text-[var(--muted-foreground)]"
                  aria-hidden
                >
                  {index + 1}
                </span>
                <p className="m-0 min-w-0 flex-1 pt-0.5 text-sm leading-relaxed text-[var(--foreground)]">
                  {item}
                </p>
                <button
                  type="button"
                  className="grid h-11 w-11 shrink-0 place-items-center rounded-md text-[var(--muted-foreground)] transition-colors hover:bg-[color-mix(in_oklab,var(--destructive)_12%,transparent)] hover:text-[var(--destructive)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]"
                  aria-label={t('common.delete', { defaultValue: 'Supprimer' })}
                  tabIndex={0}
                  onClick={() => handleRemove(index)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      handleRemove(index)
                    }
                  }}
                >
                  <Trash2 className="h-4 w-4" aria-hidden />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  )
}

/**
 * Shared Consignes + Formalités editors for product creation forms.
 */
const ImportantInfosFields = ({
  value,
  onChange,
  idPrefix = 'important-infos',
  consignesTitle,
  consignesHint,
  formalitesTitle,
  formalitesHint,
  addConsignePlaceholder,
  addFormalitePlaceholder,
  consignesEmptyTitle,
  consignesEmptyHint,
  formalitesEmptyTitle,
  formalitesEmptyHint,
}) => {
  const { t } = useTranslation()
  const infos = normalizeImportantInfos(value)

  const handlePatch = (patch) => {
    onChange?.({ ...infos, ...patch })
  }

  return (
    <div className="grid gap-4 lg:grid-cols-2 lg:items-stretch">
      <ListEditor
        id={`${idPrefix}-consignes`}
        title={
          consignesTitle ||
          t('shared.importantInfos.consignes', { defaultValue: 'Consignes' })
        }
        hint={
          consignesHint ||
          t('shared.importantInfos.consignesHint', {
            defaultValue: 'Conseils pratiques pour le client.',
          })
        }
        icon={ClipboardList}
        accentClass="bg-[color-mix(in_oklab,var(--primary)_12%,transparent)] text-[var(--primary)]"
        items={infos.consignes}
        onChange={(consignes) => handlePatch({ consignes })}
        addPlaceholder={
          addConsignePlaceholder ||
          t('shared.importantInfos.addConsigne', { defaultValue: 'Ajouter une consigne…' })
        }
        emptyTitle={
          consignesEmptyTitle ||
          t('shared.importantInfos.consignesEmpty', { defaultValue: 'Aucune consigne' })
        }
        emptyHint={
          consignesEmptyHint ||
          t('shared.importantInfos.consignesEmptyHint', {
            defaultValue: 'Ajoutez des consignes affichées à la réservation.',
          })
        }
      />
      <ListEditor
        id={`${idPrefix}-formalites`}
        title={
          formalitesTitle ||
          t('shared.importantInfos.formalites', { defaultValue: 'Documents et formalités' })
        }
        hint={
          formalitesHint ||
          t('shared.importantInfos.formalitesHint', {
            defaultValue: 'Documents et démarches requis.',
          })
        }
        icon={FileCheck2}
        accentClass="bg-[var(--secondary)] text-[var(--secondary-foreground)]"
        items={infos.formalites}
        onChange={(formalites) => handlePatch({ formalites })}
        addPlaceholder={
          addFormalitePlaceholder ||
          t('shared.importantInfos.addFormalite', { defaultValue: 'Ajouter une formalité…' })
        }
        emptyTitle={
          formalitesEmptyTitle ||
          t('shared.importantInfos.formalitesEmpty', { defaultValue: 'Aucune formalité' })
        }
        emptyHint={
          formalitesEmptyHint ||
          t('shared.importantInfos.formalitesEmptyHint', {
            defaultValue: 'Listez les documents nécessaires avant le départ.',
          })
        }
      />
    </div>
  )
}

export default ImportantInfosFields
